import React from 'react'

import { AdminContext } from 'contexts/AdminContext'

import { Table, Col, Row, Modal, Button, Form } from '_zari/core'
import { Field } from 'formik'

import * as ProductosCrud from 'services/producto.service'

import FilaBusquedaTemplate from './filasTemplates/filaBusqueda'
import FilaDetalleTemplate from './filasTemplates/filaDetalle'

const DetallesSection = ({ detalles, dispatch, tipoDeProductos, setMostrarRefrescar, incluyeIGV }) => {
  const __adminContext = React.useContext(AdminContext)
  const [modalDetalle, setModalDetalle] = React.useState({ abierto: false, fila: null, texto: '' })

  const rowHeaders = React.useMemo(() => {
    if (__adminContext.valoresConfiguracionCompania.flagUsoPresupuesto) {
      return [
        { label: '#', width: 50 },
        { label: 'REQ', width: 110 },
        { label: 'COD', width: 90 },
        { label: 'DESCRIPCIÓN' },
        { label: 'DETALLE', width: 350 },
        { label: 'CANT', width: 75 },
        { label: 'UNIDAD', width: 80 },
        { label: 'PRECIO', width: 75 },
        { label: 'PROYECTO', width: 150 },
        { label: 'FASE', width: 150 },
        { label: 'TOTAL', width: 85 },
        { label: '', width: 30 }
      ]
    }

    return [
      { label: '#', width: 50 },
      { label: 'REQ', width: 110 },
      { label: 'COD', width: 90 },
      { label: 'DESCRIPCIÓN' },
      { label: 'DETALLE', width: 350 },
      { label: 'CANT', width: 75 },
      { label: 'UNIDAD', width: 80 },
      { label: 'PRECIO', width: 75 },
      { label: 'TOTAL', width: 85 },
      { label: '', width: 30 }
    ]
  }, [__adminContext])

  const prefijoIdFila = React.useRef(1)

  const abrirModalDetalle = React.useCallback((fila) => {
    setModalDetalle({
      abierto: true,
      fila: fila,
      texto: fila.detalle || ''
    })
  }, [])

  const cerrarModalDetalle = React.useCallback(() => {
    setModalDetalle({ abierto: false, fila: null, texto: '' })
  }, [])

  const guardarDetalle = React.useCallback(() => {
    if (modalDetalle.fila) {
      dispatch({
        type: 'actualizarDetalle',
        payload: {
          _idFila: modalDetalle.fila._idFila,
          detalle: modalDetalle.texto
        }
      })
    }
    cerrarModalDetalle()
  }, [modalDetalle, dispatch, cerrarModalDetalle])

  const seleccionarProductoViaBusquedaHandler = React.useCallback(
    productoSeleccionado => {
      ProductosCrud.getProducto_Compras(productoSeleccionado.value).then(({ data }) => {
        setMostrarRefrescar(true)
        prefijoIdFila.current = prefijoIdFila.current + 1

        dispatch({
          type: 'agregar',
          payload: {
            nuevaFila: false,
            viaCodBar: false,
            detalle: '',
            ...data
          }
        })
      })
    },
    [dispatch, setMostrarRefrescar]
  )

  const seleccionarProductoViaCodigoBarraHandler = React.useCallback(
    productoSeleccionado => {
      setMostrarRefrescar(true)
      prefijoIdFila.current = prefijoIdFila.current + 1
      dispatch({
        type: 'agregar',
        payload: { 
          nuevaFila: true, 
          viaCodBar: true, 
          detalle: '',
          ...productoSeleccionado 
        }
      })
    },
    [dispatch, setMostrarRefrescar]
  )

  const actualizarFilaHandler = React.useCallback(
    data => {
      setMostrarRefrescar(true)
      const { saltarCampo, ...newData } = data
      if (saltarCampo) {
        prefijoIdFila.current = prefijoIdFila.current + 1
      }
      dispatch({ type: 'actualizar', payload: {...newData, incluyeIGV: incluyeIGV} })
    },
    [dispatch, setMostrarRefrescar, incluyeIGV]
  )

  const actualizarComentarioHandler = React.useCallback(
    ({ _idFila, comentario }) => {
      dispatch({
        type: 'actualizarComentario',
        payload: { _idFila, comentario }
      })
    },
    [dispatch]
  )

  const eliminarFilaHandler = React.useCallback(
    _idFila => {
      setMostrarRefrescar(true)
      prefijoIdFila.current = prefijoIdFila.current + 1
      dispatch({ type: 'eliminar', payload: { _idFila } })
    },
    [dispatch, setMostrarRefrescar]
  )

  const rowTemplate = React.useCallback(
    (row, key) => {
      if (row.esFilaBusqueda) {
        return <React.Fragment key={key} />
      } else {
        return (
          <FilaDetalleTemplate
            index={key}
            key={`${prefijoIdFila.current}${key}filaDetalle`}
            row={row}
            actualizarFilaHandler={actualizarFilaHandler}
            actualizarComentarioHandler={actualizarComentarioHandler}
            eliminarFilaHandler={eliminarFilaHandler}
            abrirModalDetalle={abrirModalDetalle}
          />
        )
      }
    },
    [actualizarFilaHandler, actualizarComentarioHandler, eliminarFilaHandler, abrirModalDetalle]
  )

  const filaBusquedaInfo = React.useMemo(() => {
    return detalles.find(x => x.esFilaBusqueda)
  }, [detalles])

  return (
    <>
      <Row>
        <Col md={12}>
          <Table maxRowsShown={5} filas={detalles} headRows={rowHeaders} rowTemplate={rowTemplate} />
        </Col>
      </Row>
      {filaBusquedaInfo && (
        <Row style={{ marginBottom: '3.5rem' }}>
          <Col md={12}>
            <FilaBusquedaTemplate
              key={`${prefijoIdFila.current}filaBusqueda`}
              esFilaActiva={filaBusquedaInfo.__filaActiva}
              tipoDeProductos={tipoDeProductos}
              seleccionarProductoViaBusquedaHandler={seleccionarProductoViaBusquedaHandler}
              seleccionarProductoViaCodigoBarraHandler={seleccionarProductoViaCodigoBarraHandler}
            />
          </Col>
        </Row>
      )}

      <Modal show={modalDetalle.abierto} onHide={cerrarModalDetalle}>
        <Modal.Header>
          <Modal.Title>
            Detalle - {modalDetalle.fila?.descripcion || 'Ítem'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Field name='detalle'>
            {({ field }) => (
              <Form.TextArea
                {...field}
                id='textarea-detalle'
                name='detalle'
                rows={6}
                value={modalDetalle.texto}
                onChange={(e) => setModalDetalle({
                  ...modalDetalle,
                  texto: e.target.value
                })}
                placeholder="Escriba aquí los detalles del ítem. Puede usar saltos de línea..."
                style={{ resize: 'vertical' }}
              />
            )}
          </Field>
          <div className='text-muted mt-1' style={{ fontSize: '0.78rem' }}>
            Caracteres: {modalDetalle.texto.length} | Líneas: {modalDetalle.texto.split('\n').length}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={cerrarModalDetalle}>Cancelar</Button>
          <Button onClick={guardarDetalle}>Actualizar</Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default DetallesSection